// ============================================================================
// + Add — the one button for every sub (TCSD-0371 ¶3), the prototype's sheet.
// Lead, Interviewing or Bench through the one door (add_subcontractor). A sub is
// never added as Active: a building makes them Active.
// "New — send them the application" then asks how to send THEIR OWN application
// link — the text app, or copy name / phone / link into any app (item 869) — so
// what comes back fills in this record instead of making a second one.
// What kind of work: nightly cleaning, project work, or both (item 869).
// ============================================================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../shared/components';
import { marketOptions, useMarkets } from '../../../shared/markets';
import { errorMessage } from '../../../shared/errors';
import { refreshReaders } from '../../../shared/save-path';
import { Chip, Sheet, bigPrimary, fieldCls } from './sheet';
import { addSubcontractor, applicationLinkFor, workKindFrom } from './actions';
import { SendApplicationChoice, type SendApplicationTarget } from './send-application';
import { AlreadyOnFileNotice, alreadyOnFileFrom, type AlreadyOnFileError } from '../already-on-file';
import type { PipelineStage } from '../stages';

type Where = 'new' | 'met' | 'ready';
const STAGE_FOR: Record<Where, PipelineStage> = { new: 'Lead', met: 'Meet', ready: 'Ready' };
const BUTTON_FOR: Record<Where, string> = {
  new: 'Add & send the application',
  met: 'Add to Interviewing',
  ready: 'Add to my bench',
};

export function AddSubSheet({ defaultMarkets, onClose }: { defaultMarkets: string[]; onClose: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { markets } = useMarkets();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [chosen, setChosen] = useState<string[]>(defaultMarkets);
  const [nightly, setNightly] = useState(true);
  const [projects, setProjects] = useState(false);
  const [sendTo, setSendTo] = useState<SendApplicationTarget | null>(null);
  // Once the sub is added, the sheet never goes back to the add form: a failed
  // link is retried here, so pressing Add twice can never make a second record.
  const [added, setAdded] = useState<{ providerId: string; name: string; phone: string } | null>(null);
  const [linkErr, setLinkErr] = useState<string | null>(null);

  async function makeLink(a: { providerId: string; name: string; phone: string }) {
    setLinkErr(null);
    try {
      setSendTo({ name: a.name, phone: a.phone, link: await applicationLinkFor(a.providerId) });
    } catch (e) {
      setLinkErr(errorMessage(e, 'Could not make their application link.'));
    }
  }
  const [where, setWhere] = useState<Where>('new');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [onFile, setOnFile] = useState<AlreadyOnFileError | null>(null);

  const digits = phone.replace(/[^0-9]/g, '');
  const kind = workKindFrom(nightly, projects);
  const ready = name.trim().length > 1 && digits.length >= 10 && chosen.length > 0 && kind !== null;

  async function submit(addAnyway = false) {
    if (!kind) return;
    setBusy(true);
    setErr(null);
    setOnFile(null);
    try {
      const out = await addSubcontractor({
        name: name.trim(),
        phone: phone.trim(),
        markets: chosen,
        kind,
        status: STAGE_FOR[where],
        addAnyway,
      });
      refreshReaders(['service_providers', 'sourcing_prospects']);
      if (where === 'new') {
        toast.success(`${name.trim()} added as a Lead`);
        const a = { providerId: out.provider_id, name: name.trim(), phone: phone.trim() };
        setAdded(a);
        await makeLink(a);
      } else {
        toast.success(where === 'met' ? `${name.trim()} added to Interviewing` : `${name.trim()} added to your bench`);
        onClose();
        navigate('/people/subcontractors?view=pipeline');
      }
    } catch (e) {
      const found = alreadyOnFileFrom(e);
      if (found) setOnFile(found);
      else setErr(errorMessage(e, 'Could not add the subcontractor.'));
    } finally {
      setBusy(false);
    }
  }

  if (added) {
    return (
      <Sheet title={`${added.name} is added as a Lead`} label="Send the application" onClose={onClose}>
        {sendTo ? (
          <SendApplicationChoice target={sendTo} onDone={onClose} />
        ) : linkErr ? (
          <div>
            <p className="rounded-md bg-st-danger-bg p-2 text-sm text-st-danger-fg">{linkErr}</p>
            <button type="button" className={bigPrimary} onClick={() => void makeLink(added)}>
              Try again
            </button>
          </div>
        ) : (
          <p className="text-sm text-tcs-text-muted">Making their application link…</p>
        )}
      </Sheet>
    );
  }

  return (
    <Sheet title="Add subcontractor" label="Add subcontractor" onClose={onClose}>
      <div className="space-y-3">
        <input aria-label="Name" placeholder="Name" value={name} onChange={(e) => { setName(e.target.value); setOnFile(null); }} className={fieldCls} />
        <input
          aria-label="Phone"
          placeholder="Phone"
          inputMode="tel"
          value={phone}
          onChange={(e) => { setPhone(e.target.value); setOnFile(null); }}
          className={fieldCls}
        />
        <div>
          <div className="mb-1.5 text-xs font-medium text-tcs-text-muted">Markets they can cover</div>
          <div className="flex flex-wrap gap-1.5">
            {marketOptions(markets).map((m) => (
              <Chip
                key={m}
                on={chosen.includes(m)}
                onClick={() => setChosen((c) => (c.includes(m) ? c.filter((x) => x !== m) : [...c, m]))}
              >
                {m.replace(/\s*Metro$/, '')}
              </Chip>
            ))}
          </div>
        </div>
        <div>
          <div className="mb-1.5 text-xs font-medium text-tcs-text-muted">What kind of work — one or both</div>
          <div className="flex flex-wrap gap-1.5">
            <Chip on={nightly} onClick={() => setNightly((v) => !v)}>Nightly cleaning</Chip>
            <Chip on={projects} onClick={() => setProjects((v) => !v)}>Project work (floors, snow, landscaping, etc.)</Chip>
          </div>
          {!kind && <p className="mt-1 text-xs text-st-danger-fg">Pick at least one.</p>}
        </div>
        <div>
          <div className="mb-1.5 text-xs font-medium text-tcs-text-muted">Where are they right now</div>
          <div className="flex flex-wrap gap-1.5">
            <Chip on={where === 'new'} onClick={() => setWhere('new')}>New — send them the application</Chip>
            <Chip on={where === 'met'} onClick={() => setWhere('met')}>Already met them</Chip>
            <Chip on={where === 'ready'} onClick={() => setWhere('ready')}>Ready for a building</Chip>
          </div>
        </div>
        {err && <p className="rounded-md bg-st-danger-bg p-2 text-sm text-st-danger-fg">{err}</p>}
        {onFile ? (
          <AlreadyOnFileNotice found={onFile} busy={busy} onOpen={onClose} onAddAnyway={() => void submit(true)} />
        ) : (
        <button type="button" disabled={!ready || busy} onClick={() => void submit()} className={bigPrimary}>
          {busy ? 'Adding…' : BUTTON_FOR[where]}
        </button>
        )}
        <p className="text-xs text-tcs-text-muted">
          One button for every sub — lead, interviewing or ready. A sub becomes Active when you put them on a building.
        </p>
      </div>
    </Sheet>
  );
}
